import {
  createRouter,
  createRoute,
  createRootRouteWithContext,
  redirect,
  lazyRouteComponent,
} from "@tanstack/react-router";
import type { RouterContext } from "./router.types";
import { buildPluginRoutes, buildPluginPublicRoutes } from "@/core/plugins/plugin-registry";
import { clientPluginManifests } from "@/core/plugins/client-plugin-registry";
// Eager import (not lazyRouteComponent): the page is ~30 lines with no extra
// deps, and it must render instantly as the flag-OFF fallback on every plugin
// route — not worth a chunk.
import FeatureNotAvailablePage from "./pages/FeatureNotAvailablePage";

// Augment StaticDataRouteOption so staticData is typed across all routes
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    title?: string;
    breadcrumbLabel?: string;
    description?: string;
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    requiresSuperAdmin?: boolean;
    pluginId?: string;
  }
}

// --- Root Route ---
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: lazyRouteComponent(() => import("./Root"), "Root"),
});

// --- Authenticated Layout Route (pathless) ---
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: lazyRouteComponent(() => import("./shell/AuthenticatedShell"), "AuthenticatedShell"),
});

// --- Public Routes ---
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: lazyRouteComponent(() => import("./auth/LoginPage")),
  staticData: {
    title: "Login",
    breadcrumbLabel: "Login",
  },
});

// Shared lazy component so the canonical /register route and the /signup alias
// resolve the exact same RegisterPage chunk (one import, one reference).
const registerPageComponent = lazyRouteComponent(() => import("./auth/RegisterPage"));

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: registerPageComponent,
  staticData: {
    title: "Register",
    breadcrumbLabel: "Register",
  },
});

// PUBLIC: /signup is an inbound ALIAS of /register, not a new canonical route.
// Every marketing-site CTA links to {APP_BASE_URL}/signup (Header.tsx,
// PricingTable, pricing-utils.ts) while the app's canonical route is /register,
// so without this alias every signup link 404s. Mounting the alias here (vs.
// repointing the site CTAs) needs no site rebuild, survives external /signup
// deep-links, and passes the ?plan= query through to RegisterPage. The canonical
// route stays /register — deliberately NO new AUTH_ROUTES constant (D-25).
const signupAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: registerPageComponent,
  staticData: {
    title: "Register",
    breadcrumbLabel: "Register",
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: lazyRouteComponent(() => import("./auth/ForgotPasswordPage")),
  staticData: {
    title: "Forgot Password",
    breadcrumbLabel: "Forgot Password",
  },
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: lazyRouteComponent(() => import("./auth/ResetPasswordPage")),
  staticData: {
    title: "Reset Password",
    breadcrumbLabel: "Reset Password",
  },
});

const twoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/two-factor",
  component: lazyRouteComponent(() => import("./auth/TwoFactorPage")),
  staticData: {
    title: "Two-Factor Authentication",
    breadcrumbLabel: "Two-Factor",
  },
});

const checkEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/check-email",
  component: lazyRouteComponent(() => import("./auth/CheckEmailPage")),
  staticData: {
    title: "Check Your Email",
    breadcrumbLabel: "Check Email",
  },
});

// PUBLIC: the signup verification email links here with ?token=... — the user
// has no session yet, so this must NOT live under the authenticated guard.
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: lazyRouteComponent(() => import("./auth/VerifyEmailPage")),
  staticData: {
    title: "Verify Email",
    breadcrumbLabel: "Verify Email",
  },
});

// PUBLIC: invited users have no account yet, so this must NOT live under the
// authenticated guard (it would bounce them to /login before they can sign up).
const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/accept",
  component: lazyRouteComponent(() => import("./auth/InviteAcceptPage")),
  staticData: {
    title: "Accept Invitation",
    breadcrumbLabel: "Accept Invitation",
  },
});

// PUBLIC: email verification may not auto-login the user, so /welcome must be
// reachable without authentication (WelcomePage degrades gracefully when the
// org-name query returns UNAUTHORIZED).
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: lazyRouteComponent(() => import("./auth/WelcomePage")),
  staticData: {
    title: "Welcome",
    breadcrumbLabel: "Welcome",
  },
});

// NOTE: the public /pricing route now lives in the billing plugin's manifest
// `publicRoutes[]` (09b-01, D-05) and is mounted session-less under rootRoute via
// `buildPluginPublicRoutes` below — so it strips cleanly with the billing plugin.

// --- Protected Routes ---
const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/dashboard",
  component: lazyRouteComponent(() => import("./pages/Dashboard")),
  staticData: {
    title: "Dashboard",
    breadcrumbLabel: "Dashboard",
    requiresAuth: true,
  },
});

// --- Settings Routes ---
const settingsLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("./settings/SettingsLayout")),
  staticData: {
    title: "Settings",
    breadcrumbLabel: "Settings",
    requiresAuth: true,
  },
});

// Index route for /settings — fixes the empty-screen defect (08.7-15). Renders
// the settings index (PageHeader + section rows + Appearance) so /settings
// ALWAYS shows usable content instead of an empty Outlet.
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./settings/SettingsIndex")),
  staticData: {
    title: "Settings",
    breadcrumbLabel: "Settings",
    requiresAuth: true,
  },
});

const accountSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("./settings/AccountSettings")),
  staticData: {
    title: "Account Settings",
    breadcrumbLabel: "Account",
    requiresAuth: true,
  },
});

// NOTE: /settings/billing now lives in the billing plugin manifest routes[]
// (09b-01, D-05), mounted under authenticatedRoute via buildPluginRoutes.

const securitySettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/security",
  component: lazyRouteComponent(() => import("./settings/SecuritySettings")),
  staticData: {
    title: "Security Settings",
    breadcrumbLabel: "Security",
    requiresAuth: true,
  },
});

// NOTE: /settings/privacy now lives in the privacy plugin manifest routes[]
// (09b-01, D-05) → ConsentSettingsContainer, mounted under authenticatedRoute
// via buildPluginRoutes.

// --- Admin Routes ---
const adminLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/admin",
  // Allow both admins and super admins. This mirrors the backend role hierarchy
  // (SUPER_ADMIN > TENANT_ADMIN > USER) where super admins bypass all role checks.
  beforeLoad: ({ context }) => {
    if (!context.auth.isAdmin && !context.auth.isSuperAdmin) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: lazyRouteComponent(() => import("./admin/AdminLayout")),
  staticData: {
    title: "Admin",
    breadcrumbLabel: "Admin",
    requiresAuth: true,
    requiresAdmin: true,
  },
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./admin/Dashboard")),
  staticData: {
    title: "Admin Dashboard",
    breadcrumbLabel: "Dashboard",
    requiresAuth: true,
    requiresAdmin: true,
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/users",
  component: lazyRouteComponent(() => import("./admin/Users")),
  staticData: {
    title: "Manage Users",
    breadcrumbLabel: "Users",
    requiresAuth: true,
    requiresAdmin: true,
  },
});

const adminTenantsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/tenants",
  // Parent adminLayoutRoute allows any admin or super admin. This child guard
  // further restricts to super admins only, since tenant management is a
  // platform-level operation.
  beforeLoad: ({ context }) => {
    if (!context.auth.isSuperAdmin) {
      throw redirect({ to: "/admin" });
    }
  },
  component: lazyRouteComponent(() => import("./admin/Tenants")),
  staticData: {
    title: "Manage Tenants",
    breadcrumbLabel: "Tenants",
    requiresAuth: true,
    requiresAdmin: true,
    requiresSuperAdmin: true,
  },
});

// The /showcase route is the single VR capture vehicle for plan 07 (D-14): it
// renders every primitive + composite in a state grid for human review AND as
// the Playwright screenshot target. It is super-admin gated at the CLIENT route
// level — the child beforeLoad below redirects non-super-admins so the route
// component never RENDERS for tenant users (mirrors the adminTenantsRoute
// platform-level guard).
//
// SCOPE HONESTY (review MEDIUM-5): this is super-admin-only route RENDERING, not
// a hard confidentiality boundary. The lazy showcase JS chunk may still be
// fetchable by a determined client; server-side chunk gating is an explicit
// non-goal this phase. The showcase contains only component demos (no real
// tenant data), so the residual exposure is accepted.
const showcaseRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/showcase",
  beforeLoad: ({ context }) => {
    if (!context.auth.isSuperAdmin) {
      throw redirect({ to: "/admin" });
    }
  },
  component: lazyRouteComponent(() => import("./showcase/Showcase")),
  staticData: {
    title: "Component Showcase",
    breadcrumbLabel: "Showcase",
    requiresAuth: true,
    requiresAdmin: true,
    requiresSuperAdmin: true,
  },
});

const adminFeatureFlagsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/feature-flags",
  component: lazyRouteComponent(() => import("./admin/FeatureFlagsPage")),
  staticData: {
    title: "Feature Flags",
    breadcrumbLabel: "Feature Flags",
    requiresAuth: true,
    requiresAdmin: true,
  },
});

const adminPluginsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/plugins",
  component: lazyRouteComponent(() => import("./admin/PluginManagementPage")),
  staticData: {
    title: "Plugin Management",
    breadcrumbLabel: "Plugins",
    requiresAuth: true,
    requiresAdmin: true,
  },
});

// NOTE: /admin/plugins/sample-notes now lives in the sample-notes plugin
// manifest routes[] (09b-01, D-05), mounted under authenticatedRoute via
// buildPluginRoutes. Server-side adminProcedure RBAC is the authoritative gate
// (T-09b-01). The defense-in-depth client redirect that adminLayoutRoute
// carried is restored via the manifest `requiresAdmin`/`requiresSuperAdmin`
// marker (09b, WR-01) — buildPluginRoutes injects the same beforeLoad redirect,
// and the marker strips with the plugin so no core edit is reintroduced.

// --- 404 Catch-all Route ---
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: lazyRouteComponent(() => import("./pages/NotFoundPage")),
  staticData: {
    title: "Page Not Found",
    breadcrumbLabel: "Not Found",
  },
});

// --- Plugin Routes ---
// All plugin routes are included in the static route tree regardless of
// whether the corresponding feature flag is enabled. This is intentional:
// TanStack Router builds its route tree at module-evaluation time (before
// feature flags are evaluated via async API calls). Runtime feature gating
// is per-render: buildPluginRoutes wraps each route component in a
// FeatureGate on the plugin's flag, rendering FeatureNotAvailablePage when
// the flag is off (flagFallback below) instead of a blank screen.
const allPluginFlagKeys = new Set(clientPluginManifests.map((p) => p.featureFlagKey));
const pluginRoutes = buildPluginRoutes(
  authenticatedRoute,
  clientPluginManifests,
  allPluginFlagKeys,
  { flagFallback: FeatureNotAvailablePage },
);
// Public, session-less plugin routes (09b-01, D-05) — e.g. billing's /pricing.
// Mounted directly under rootRoute (no auth guard), alongside the other public
// routes. Strips cleanly when the owning plugin's manifest is removed.
const pluginPublicRoutes = buildPluginPublicRoutes(rootRoute, clientPluginManifests);

// --- Route Tree ---
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  signupAliasRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  twoFactorRoute,
  checkEmailRoute,
  verifyEmailRoute,
  inviteAcceptRoute,
  welcomeRoute,
  ...pluginPublicRoutes,
  authenticatedRoute.addChildren([
    dashboardRoute,
    settingsLayoutRoute.addChildren([
      settingsIndexRoute,
      accountSettingsRoute,
      securitySettingsRoute,
    ]),
    adminLayoutRoute.addChildren([
      adminDashboardRoute,
      adminUsersRoute,
      adminTenantsRoute,
      showcaseRoute,
      adminFeatureFlagsRoute,
      adminPluginsRoute,
    ]),
    ...pluginRoutes,
  ]),
  notFoundRoute,
]);

// --- Router Instance ---
export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: "intent",
    defaultErrorComponent: lazyRouteComponent(() => import("./pages/ErrorPage")),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}

// Export individual routes for testing
export const routes = {
  root: rootRoute,
  authenticated: authenticatedRoute,
  index: indexRoute,
  login: loginRoute,
  register: registerRoute,
  signup: signupAliasRoute,
  forgotPassword: forgotPasswordRoute,
  resetPassword: resetPasswordRoute,
  twoFactor: twoFactorRoute,
  checkEmail: checkEmailRoute,
  verifyEmail: verifyEmailRoute,
  inviteAccept: inviteAcceptRoute,
  welcome: welcomeRoute,
  dashboard: dashboardRoute,
  settingsLayout: settingsLayoutRoute,
  settingsIndex: settingsIndexRoute,
  accountSettings: accountSettingsRoute,
  securitySettings: securitySettingsRoute,
  adminLayout: adminLayoutRoute,
  adminDashboard: adminDashboardRoute,
  adminUsers: adminUsersRoute,
  adminTenants: adminTenantsRoute,
  showcase: showcaseRoute,
  adminFeatureFlags: adminFeatureFlagsRoute,
  adminPlugins: adminPluginsRoute,
  notFound: notFoundRoute,
} as const;
